import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";
import "flag-icons/css/flag-icons.min.css";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LANGS = [
  { code: "fr", label: "FR", country: "fr", name: "Français" },
  { code: "en", label: "EN", country: "gb", name: "English" },
  { code: "es", label: "ES", country: "es", name: "Español" },
  { code: "nl", label: "NL", country: "nl", name: "Nederlands" },
];

const Flag = ({ country, size = 20 }) => (
  <span
    className="inline-block rounded-full overflow-hidden ring-1 ring-white/20 shrink-0 shadow-sm"
    style={{ width: size, height: size }}
  >
    <span className={`fi fis fi-${country}`} style={{ width: "100%", height: "100%", display: "block" }} />
  </span>
);

export function LanguageSwitcher({ className = "" }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const current = (i18n.language || "fr").slice(0, 2);
  const active = LANGS.find((l) => l.code === current) || LANGS[0];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
    // Persiste le choix : localStorage (visiteurs) + profil serveur (connectés),
    // sinon `applyUserLang` au prochain chargement écraserait ce choix.
    if (user) api.put("/preferences", { lang: code }).catch(() => {});
  };

  return (
    <div className={`relative ${className}`} ref={ref} data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="lang-trigger"
        aria-label="Choisir la langue"
        aria-expanded={open}
        className="group inline-flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-full border border-white/12 bg-white/[0.04] hover:bg-white/[0.08] hover:border-primary/40 backdrop-blur-md transition-all duration-300"
      >
        <Flag country={active.country} size={22} />
        <span className="text-xs font-semibold tracking-wide text-foreground/80 group-hover:text-primary transition-colors">{active.label}</span>
        <ChevronDown size={14} className={`text-foreground/40 group-hover:text-primary transition-all duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 mt-2 w-52 z-[70] origin-top-right rounded-2xl border border-primary/20 bg-[#150d07]/90 backdrop-blur-2xl shadow-2xl shadow-black/50 overflow-hidden p-1.5"
            data-testid="lang-menu"
            role="listbox"
          >
            <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-[0.2em] text-primary/50">Langue</p>
            {LANGS.map((l, i) => {
              const on = l.code === current;
              return (
                <motion.button
                  key={l.code}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.03 + i * 0.04 }}
                  onClick={() => choose(l.code)}
                  data-testid={`lang-${l.code}`}
                  role="option"
                  aria-selected={on}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                    on ? "bg-primary/12 text-primary" : "text-foreground/70 hover:bg-white/[0.06] hover:text-foreground/95"
                  }`}
                >
                  <Flag country={l.country} size={24} />
                  <span className="flex-1 text-sm font-medium">{l.name}</span>
                  <span className={`text-[10px] font-semibold tracking-wider ${on ? "text-primary/70" : "text-foreground/30"}`}>{l.label}</span>
                  {on && <Check size={15} className="text-primary" />}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
